import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  getDocs,
  query,
  orderBy,
  where,
  limit,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

// Firebase Configuration
// Production credentials for Dulce's Journey
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyARMA7fS7zsyFcsebfLug1ScFdoXHUwseI",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "dulce-s-journey.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "dulce-s-journey",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "dulce-s-journey.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "542329046414",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:542329046414:web:040eec31175236a9b510df",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-9WWH36Y75G"
};

// Initialize Firebase
let app;
let auth;
let db;
const verboseFirebaseLogs = process.env.REACT_APP_VERBOSE_FIREBASE_LOGS === 'true';

const logFirebase = (...args) => {
  if (verboseFirebaseLogs) {
    console.log(...args);
  }
};

const toPlainObject = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
};

const toDateKey = (date = new Date()) => {
  return date.toISOString().slice(0, 10);
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getMonthKey = (dateKey) => {
  return String(dateKey || '').slice(0, 7);
};

const getMonthDateRange = (monthKey) => {
  const start = `${monthKey}-01`;
  const nextMonthDate = new Date(`${start}T00:00:00.000Z`);
  nextMonthDate.setUTCMonth(nextMonthDate.getUTCMonth() + 1);
  const end = addDays(nextMonthDate, -1).toISOString().slice(0, 10);
  return { start, end };
};

const getAuthProfileFields = (firebaseUser = auth?.currentUser) => {
  if (!firebaseUser) {
    return {};
  }

  return {
    email: firebaseUser.email || null,
    authDisplayName: firebaseUser.displayName || null,
    authPhotoURL: firebaseUser.photoURL || null,
    authProvider: firebaseUser.providerData?.[0]?.providerId || null,
  };
};

const isPaidUser = (userData = {}) => {
  if (!userData || typeof userData !== 'object') {
    return false;
  }

  return userData.subscriptionTier === 'paid'
    || userData.plan === 'paid'
    || userData.fitHistoryRetention === 'unlimited'
    || userData.fitUnlimitedHistory === true
    || userData.isPaidSubscriber === true;
};

const getGoogleFitRetentionDays = (userData = {}) => {
  return isPaidUser(userData) ? null : 365;
};

const cleanupGoogleFitDailyRetention = async (userId, retentionDays) => {
  if (!Number.isFinite(retentionDays) || retentionDays <= 0) {
    return;
  }

  const cutoffDate = addDays(new Date(), -retentionDays);
  const cutoffKey = toDateKey(cutoffDate);
  const fitDailyRef = collection(db, 'users', userId, 'fitDaily');
  const allDaily = await getDocs(fitDailyRef);

  const deletions = [];
  allDaily.forEach((docSnap) => {
    const data = docSnap.data();
    if (data?.dateKey && data.dateKey < cutoffKey) {
      deletions.push(deleteDoc(docSnap.ref));
    }
  });

  if (deletions.length > 0) {
    await Promise.all(deletions);
    logFirebase(` Removed ${deletions.length} expired Google Fit daily records`);
  }
};

const refreshGoogleFitMonthlyRollup = async (userId, monthKey) => {
  const { start, end } = getMonthDateRange(monthKey);
  const fitDailyRef = collection(db, 'users', userId, 'fitDaily');
  const monthQuery = query(
    fitDailyRef,
    where('dateKey', '>=', start),
    where('dateKey', '<=', end),
    orderBy('dateKey', 'asc')
  );
  const monthSnapshot = await getDocs(monthQuery);

  const rows = [];
  monthSnapshot.forEach((docSnap) => {
    rows.push(docSnap.data());
  });

  const totalDays = rows.length;
  const totalSteps = rows.reduce((sum, row) => sum + (Number.isFinite(row.steps) ? row.steps : 0), 0);
  const avgSteps = totalDays ? Math.round(totalSteps / totalDays) : 0;
  const maxSteps = rows.reduce((max, row) => Math.max(max, Number.isFinite(row.steps) ? row.steps : 0), 0);

  const hrRows = rows.filter((row) => Number.isFinite(row.heartRateAvg) && row.heartRateAvg > 0);
  const avgHeartRate = hrRows.length
    ? Math.round(hrRows.reduce((sum, row) => sum + row.heartRateAvg, 0) / hrRows.length)
    : 0;

  const restingRows = rows.filter((row) => Number.isFinite(row.restingHeartRateAvg) && row.restingHeartRateAvg > 0);
  const avgRestingHeartRate = restingRows.length
    ? Math.round(restingRows.reduce((sum, row) => sum + row.restingHeartRateAvg, 0) / restingRows.length)
    : 0;

  const activeDays = rows.filter((row) => Number.isFinite(row.steps) && row.steps >= 6000).length;
  const goalDays = rows.filter((row) => Number.isFinite(row.steps) && row.steps >= 8000).length;

  const monthRef = doc(db, 'users', userId, 'fitMonthly', monthKey);
  await setDoc(monthRef, {
    monthKey,
    totalDays,
    totalSteps,
    avgSteps,
    maxSteps,
    avgHeartRate,
    avgRestingHeartRate,
    activeDays,
    goalDays,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const initializeFirebase = async () => {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    logFirebase(' Firebase initialized with Firestore');
    return { app, auth, db };
  } catch (error) {
    console.error('Firebase initialization error:', error);
    throw error;
  }
};

// Get current user
export const getCurrentUser = () => {
  return auth?.currentUser;
};

// Watch auth state changes
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error('Google sign-in failed:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign out failed:', error);
    throw error;
  }
};

// ===== USER DATA MANAGEMENT =====

export const saveUserData = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...toPlainObject(userData),
      user: toPlainObject(userData),
      ...getAuthProfileFields(),
      lastUpdated: serverTimestamp(),
    }, { merge: true });
    logFirebase(' User data saved to Firestore');
  } catch (error) {
    console.error('Error saving user data:', error);
    throw error;
  }
};

export const saveAuthProfile = async (firebaseUser) => {
  if (!firebaseUser?.uid) {
    return;
  }

  try {
    const userRef = doc(db, 'users', firebaseUser.uid);
    await setDoc(userRef, {
      ...getAuthProfileFields(firebaseUser),
      authUpdated: serverTimestamp(),
    }, { merge: true });
    logFirebase(' Auth profile saved to Firestore');
  } catch (error) {
    console.error('Error saving auth profile:', error);
    throw error;
  }
};

export const getUserData = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// ===== TASKS MANAGEMENT =====

export const saveTasks = async (userId, tasks) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      tasks: tasks,
      tasksUpdated: serverTimestamp()
    }, { merge: true });
    logFirebase(' Tasks saved to Firestore');
  } catch (error) {
    console.error('Error saving tasks:', error);
    throw error;
  }
};

export const getTasks = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists() && snapshot.data().tasks) {
      return snapshot.data().tasks;
    }
    return [];
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

// ===== EXERCISE HISTORY =====

export const saveExerciseHistory = async (userId, history) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      exerciseHistory: history,
      historyUpdated: serverTimestamp()
    }, { merge: true });
    logFirebase(' Exercise history saved to Firestore');
  } catch (error) {
    console.error('Error saving exercise history:', error);
    throw error;
  }
};

export const getExerciseHistory = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists() && snapshot.data().exerciseHistory) {
      return snapshot.data().exerciseHistory;
    }
    return {};
  } catch (error) {
    console.error('Error fetching exercise history:', error);
    throw error;
  }
};

// ===== WORKOUT LOG MANAGEMENT =====

export const saveWorkoutLog = async (userId, workoutData) => {
  try {
    const workoutLogsRef = collection(db, 'users', userId, 'workoutLogs');
    await addDoc(workoutLogsRef, {
      ...workoutData,
      timestamp: serverTimestamp(),
    });
    logFirebase(' Workout logged to Firestore');
  } catch (error) {
    console.error('Error saving workout log:', error);
    throw error;
  }
};

export const getWorkoutLogs = async (userId) => {
  try {
    const workoutLogsRef = collection(db, 'users', userId, 'workoutLogs');
    const q = query(workoutLogsRef, orderBy('timestamp', 'desc'));
    const snapshot = await getDocs(q);
    
    const logs = {};
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // Convert Firestore timestamp to ISO string for compatibility
      const timestamp = data.timestamp?.toDate?.()?.toISOString() || new Date().toISOString();
      logs[timestamp] = { ...data, id: docSnap.id };
    });
    
    return logs;
  } catch (error) {
    console.error('Error fetching workout logs:', error);
    throw error;
  }
};

// ===== BUDDY DATA MANAGEMENT =====

export const saveBuddyData = async (userId, buddyData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const plainBuddyData = toPlainObject(buddyData) || {};
    await setDoc(userRef, {
      buddy: plainBuddyData,
      buddyUpdated: serverTimestamp()
    }, { merge: true });
    logFirebase(' Buddy data saved to Firestore');
  } catch (error) {
    console.error('Error saving buddy data:', error);
    throw error;
  }
};

export const getBuddyData = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists() && snapshot.data().buddy) {
      return snapshot.data().buddy;
    }
    return null;
  } catch (error) {
    console.error('Error fetching buddy data:', error);
    throw error;
  }
};

// ===== GOOGLE FIT DATA MANAGEMENT =====

export const saveGoogleFitSnapshot = async (userId, fitData) => {
  try {
    const safeFitData = toPlainObject(fitData) || {};
    const dateKey = safeFitData.dateKey || toDateKey();
    const userRef = doc(db, 'users', userId);
    const userSnapshot = await getDoc(userRef);
    const retentionDays = getGoogleFitRetentionDays(userSnapshot.exists() ? userSnapshot.data() : {});

    const latestPayload = {
      dateKey,
      steps: Number.isFinite(safeFitData.steps) ? safeFitData.steps : 0,
      heartRateAvg: Number.isFinite(safeFitData.heartRateAvg) ? safeFitData.heartRateAvg : 0,
      restingHeartRateAvg: Number.isFinite(safeFitData.restingHeartRateAvg)
        ? safeFitData.restingHeartRateAvg
        : (Number.isFinite(safeFitData.heartRateAvg) ? safeFitData.heartRateAvg : 0),
      source: safeFitData.source || 'google-fit',
      syncedAt: serverTimestamp(),
    };

    await setDoc(userRef, {
      fitLatest: latestPayload,
      fitUpdated: serverTimestamp(),
    }, { merge: true });

    const fitDailyRef = doc(db, 'users', userId, 'fitDaily', dateKey);
    await setDoc(fitDailyRef, {
      ...latestPayload,
      dateKey,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    await refreshGoogleFitMonthlyRollup(userId, getMonthKey(dateKey));
    await cleanupGoogleFitDailyRetention(userId, retentionDays);

    logFirebase(' Google Fit snapshot saved to Firestore');
  } catch (error) {
    console.error('Error saving Google Fit snapshot:', error);
    throw error;
  }
};

export const getGoogleFitLatest = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists() && snapshot.data().fitLatest) {
      return snapshot.data().fitLatest;
    }
    return null;
  } catch (error) {
    console.error('Error fetching Google Fit latest snapshot:', error);
    throw error;
  }
};

export const getGoogleFitDailyHistory = async (userId, days = 60) => {
  try {
    const fitDailyRef = collection(db, 'users', userId, 'fitDaily');
    const fitQuery = query(fitDailyRef, orderBy('dateKey', 'desc'), limit(days));
    const snapshot = await getDocs(fitQuery);

    const history = [];
    snapshot.forEach((docSnap) => {
      history.push({ id: docSnap.id, ...docSnap.data() });
    });

    return history;
  } catch (error) {
    console.error('Error fetching Google Fit history:', error);
    throw error;
  }
};

// ===== SYNC LOCAL STORAGE TO FIRESTORE =====

export const syncLocalDataToFirebase = async (userId, localData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...getAuthProfileFields(),
      user: localData.user || {},
      tasks: localData.tasks || [],
      buddy: toPlainObject(localData.buddy) || {},
      exerciseHistory: localData.exerciseHistory || {},
      fitLatest: toPlainObject(localData.googleFitLatest) || null,
      syncedAt: serverTimestamp(),
    }, { merge: true });
    
    // Sync workout logs to subcollection if they exist
    if (localData.workoutLogs && Object.keys(localData.workoutLogs).length > 0) {
      const workoutLogsRef = collection(db, 'users', userId, 'workoutLogs');
      for (const [timestamp, logData] of Object.entries(localData.workoutLogs)) {
        await addDoc(workoutLogsRef, {
          ...logData,
          timestamp: new Date(timestamp),
        });
      }
    }
    
    logFirebase(' Local data synced to Firestore');
  } catch (error) {
    console.error('Error syncing data to Firestore:', error);
    throw error;
  }
};

// ===== SYNC FIRESTORE TO LOCAL STORAGE =====

export const syncFirebaseToLocalData = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      
      // Get workout logs from subcollection
      const workoutLogsRef = collection(db, 'users', userId, 'workoutLogs');
      const logsSnapshot = await getDocs(workoutLogsRef);
      
      const workoutLogs = {};
      logsSnapshot.forEach((docSnap) => {
        const logData = docSnap.data();
        const timestamp = logData.timestamp?.toDate?.()?.toISOString() || new Date().toISOString();
        workoutLogs[timestamp] = { ...logData, id: docSnap.id };
      });
      
      return {
        user: data.user || {
          id: data.id,
          name: data.name,
          xp: data.xp,
          level: data.level,
          streakDays: data.streakDays,
          achievements: data.achievements,
          createdAt: data.createdAt,
          lastActiveDate: data.lastActiveDate,
          hasSelectedBuddy: data.hasSelectedBuddy,
          buddyPreferences: data.buddyPreferences,
        },
        tasks: data.tasks || [],
        buddy: data.buddy || {},
        exerciseHistory: data.exerciseHistory || {},
        googleFitLatest: data.fitLatest || null,
        workoutLogs: workoutLogs,
      };
    }
    return null;
  } catch (error) {
    console.error('Error syncing Firestore data:', error);
    throw error;
  }
};

export { auth, db };
